import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Footer from "@/components/Footer";

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 to-white">
      {/* Header */}
      <header className="bg-gradient-to-r from-pink-600 via-purple-600 to-pink-600 text-white py-6">
        <div className="container mx-auto px-4">
          <Link to="/" className="inline-flex items-center gap-2 text-white/90 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>
            Privacy Policy
          </h1>
          <p className="text-white/80 mt-2">Last updated: {new Date().toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}</p>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="prose prose-pink max-w-none">
          
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">1. Introduction</h2>
            <p className="text-gray-600 leading-relaxed">
              MUAS Academy ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your personal information when you visit our website (muasacademyranchi.com), enroll in our courses, or interact with our services. By using our services, you consent to the practices described in this policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">2. Information We Collect</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              We collect the following types of personal information:
            </p>
            
            <h3 className="text-xl font-semibold text-gray-700 mt-4 mb-2">Personal Identification Information</h3>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Full name</li>
              <li>Phone number(s)</li>
              <li>Email address</li>
              <li>Residential address</li>
              <li>Date of birth</li>
              <li>Government ID details (for verification purposes)</li>
              <li>Photographs (for student records and certificates)</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-700 mt-4 mb-2">Payment Information</h3>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Payment method details</li>
              <li>Transaction records</li>
              <li>Bank account details (for refunds, if applicable)</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-700 mt-4 mb-2">Technical Information</h3>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>IP address</li>
              <li>Browser type and version</li>
              <li>Device information</li>
              <li>Pages visited and time spent on our website</li>
              <li>Referring website or source</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">3. How We Use Your Information</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              We use the collected information for the following purposes:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li><strong>Course Enrollment:</strong> To process your enrollment, maintain student records, and issue certificates</li>
              <li><strong>Communication:</strong> To send course updates, schedules, reminders, and important announcements</li>
              <li><strong>Payment Processing:</strong> To process fees, issue receipts, and handle refunds</li>
              <li><strong>Marketing:</strong> To send promotional materials, newsletters, and information about new courses (with your consent; you can opt-out anytime)</li>
              <li><strong>Improvement:</strong> To analyze website usage and improve our services</li>
              <li><strong>Legal Compliance:</strong> To comply with applicable laws and regulations</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">4. Cookies and Website Analytics</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              Our website uses cookies and similar tracking technologies to enhance your browsing experience. We also use:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li><strong>Google Analytics:</strong> To understand how visitors interact with our website, including pages visited, time spent, and traffic sources</li>
              <li><strong>Google Ads:</strong> To display relevant advertisements and measure ad performance. Google may use cookies to show you ads based on your visit to our website</li>
              <li><strong>Essential Cookies:</strong> Required for the website to function properly (e.g., login sessions)</li>
            </ul>
            <p className="text-gray-600 leading-relaxed mt-4">
              You can control cookie preferences through your browser settings. Disabling cookies may affect some website functionality.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">5. Data Storage and Security</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              We implement appropriate technical and organizational measures to protect your personal data:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Secure servers with encryption for data transmission</li>
              <li>Access controls limiting who can view personal information</li>
              <li>Regular security assessments and updates</li>
              <li>Physical security measures at our premises</li>
            </ul>
            <p className="text-gray-600 leading-relaxed mt-4">
              While we strive to protect your information, no method of transmission over the internet is 100% secure. We cannot guarantee absolute security.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">6. Third-Party Sharing</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              We may share your information with:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li><strong>Payment Processors:</strong> To process course fee payments securely</li>
              <li><strong>Service Providers:</strong> Third parties who assist in operating our website and services (under confidentiality agreements)</li>
              <li><strong>Legal Authorities:</strong> When required by law or to protect our rights</li>
              <li><strong>Marketing Partners:</strong> Only with your explicit consent for promotional purposes</li>
            </ul>
            <p className="text-gray-600 leading-relaxed mt-4">
              We do not sell your personal information to third parties.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">7. Your Rights</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              You have the following rights regarding your personal data:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li><strong>Access:</strong> Request a copy of the personal data we hold about you</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete data</li>
              <li><strong>Deletion:</strong> Request deletion of your data (subject to legal retention requirements)</li>
              <li><strong>Opt-out:</strong> Unsubscribe from marketing communications at any time</li>
              <li><strong>Portability:</strong> Request your data in a commonly used format</li>
            </ul>
            <p className="text-gray-600 leading-relaxed mt-4">
              To exercise these rights, please contact us using the details provided below.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">8. Data Retention</h2>
            <p className="text-gray-600 leading-relaxed">
              We retain your personal information for as long as necessary to fulfill the purposes outlined in this policy, typically:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2 mt-4">
              <li>Student records: 7 years after course completion</li>
              <li>Payment records: As required by tax laws (typically 7-10 years)</li>
              <li>Marketing data: Until you opt-out or withdraw consent</li>
              <li>Website analytics: 26 months</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">9. Children's Privacy</h2>
            <p className="text-gray-600 leading-relaxed">
              Our services are not intended for children under 16 without parental consent. If a minor wishes to enroll, we require parental/guardian consent and involvement. If we become aware that we have collected data from a child without proper consent, we will take steps to delete such information.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">10. Changes to This Policy</h2>
            <p className="text-gray-600 leading-relaxed">
              We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated revision date. We encourage you to review this policy periodically.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">11. Contact Us</h2>
            <p className="text-gray-600 leading-relaxed">
              For any privacy-related queries, concerns, or to exercise your rights, please contact us:
            </p>
            <div className="bg-gray-50 p-4 rounded-lg mt-4">
              <p className="text-gray-700"><strong>MUAS Academy - Privacy Team</strong></p>
              <p className="text-gray-600">C-127, Harmu Housing Colony, Harmu Ranchi 834002</p>
              <p className="text-gray-600">Jharkhand, India</p>
              <p className="text-gray-600">Phone: +91 6201542274, +91 9773682082</p>
              <p className="text-gray-600">Email: muasacademy.ranchi@gmail.com</p>
            </div>
          </section>

        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
}
